import { castSummonerSpell, SUMMONER_SPELLS, buildSummonerHudSlot } from '../domain/summonerSpells.js';
import { isSummonerSpellAllowed } from '../domain/map/summonerSpellMapRules.js';
import { resolveOutgoingHealAmount } from '../domain/map/aramRules.js';

/** LoL unit → live sandbox xz (matches attackRange / 100). */
export const SUMMONER_WORLD_SCALE = 0.01;

const TARGETED = new Set(['ignite', 'exhaust', 'smite']);
const CC_TYPES = new Set(['stun', 'root', 'slow', 'silence']);

/**
 * SummonerSpellCaster — D/F slots, cooldowns, apply domain cast results to entities.
 */
export class SummonerSpellCaster {
  constructor({ spellD = 'flash', spellF = 'ignite', mapId = null } = {}) {
    this.spellD = spellD;
    this.spellF = spellF;
    this.mapId = mapId;
    this.cooldownD = 0;
    this.cooldownF = 0;
  }

  setMapId(mapId) {
    this.mapId = mapId || null;
  }

  setSpells(spellD, spellF) {
    if (!SUMMONER_SPELLS[spellD] || !SUMMONER_SPELLS[spellF]) return false;
    if (spellD === spellF) return false;
    if (this.mapId) {
      if (!isSummonerSpellAllowed(spellD, this.mapId)) return false;
      if (!isSummonerSpellAllowed(spellF, this.mapId)) return false;
    }
    this.spellD = spellD;
    this.spellF = spellF;
    return true;
  }

  tick(dt, caster = null) {
    if (this.cooldownD > 0) this.cooldownD = Math.max(0, this.cooldownD - dt);
    if (this.cooldownF > 0) this.cooldownF = Math.max(0, this.cooldownF - dt);
    if (caster) tickShield(caster, dt);
  }

  castSlot(slot, ctx = {}) {
    const key = String(slot || '').toUpperCase();
    const spellId = key === 'D' ? this.spellD : key === 'F' ? this.spellF : null;
    if (!spellId) return { success: false, reason: 'INVALID_SLOT' };
    const cdProp = key === 'D' ? 'cooldownD' : 'cooldownF';
    if (this[cdProp] > 0) return { success: false, reason: 'ON_COOLDOWN' };
    if (!ctx.caster || ctx.caster.isDead) return { success: false, reason: 'CASTER_DEAD' };
    if (TARGETED.has(spellId) && !ctx.target) {
      return { success: false, reason: 'NO_TARGET' };
    }
    if (TARGETED.has(spellId) && !isInSpellRange(spellId, ctx.caster, ctx.target)) {
      return { success: false, reason: 'OUT_OF_RANGE' };
    }
    if (this.mapId && !isSummonerSpellAllowed(spellId, this.mapId)) {
      return { success: false, reason: 'BANNED_ON_MAP', spellId, mapId: this.mapId };
    }

    const planeCaster = { ...ctx.caster, x: ctx.caster.x, y: ctx.caster.z ?? ctx.caster.y ?? 0 };
    const planeTarget = buildPlaneTarget(ctx);
    const result = castSummonerSpell(spellId, planeCaster, planeTarget, {
      worldScale: SUMMONER_WORLD_SCALE
    });
    if (!result.success) return result;

    applyCastResult(result, ctx);
    this[cdProp] = result.cooldown || SUMMONER_SPELLS[spellId].cooldown;
    return { success: true, spellId, result };
  }

  getHudSnapshot() {
    return {
      D: buildSummonerHudSlot(this.spellD, this.cooldownD),
      F: buildSummonerHudSlot(this.spellF, this.cooldownF)
    };
  }
}

function isInSpellRange(spellId, caster, target) {
  const spell = SUMMONER_SPELLS[spellId];
  if (!spell || spell.range === undefined || spell.range === null) return true;
  const max = spell.range * SUMMONER_WORLD_SCALE;
  const dy = (target.z ?? target.y ?? 0) - (caster.z ?? caster.y ?? 0);
  return Math.hypot(target.x - caster.x, dy) <= max + 0.35;
}

function buildPlaneTarget(ctx) {
  if (ctx.target) {
    return { x: ctx.target.x, y: ctx.target.z ?? ctx.target.y ?? 0, ...ctx.target };
  }
  if (ctx.cursor) {
    return { x: ctx.cursor.x, y: ctx.cursor.z ?? ctx.cursor.y ?? 0 };
  }
  return null;
}

function applyCastResult(result, ctx) {
  const { caster, target, allies = [] } = ctx;
  if (result.caster) applyFlash(caster, result.caster);
  if (result.damage !== undefined && result.damage !== null && target) {
    target.takeDamage(result.damage, caster, result.damageType || 'true');
  }
  if (result.heal !== undefined && result.heal !== null) applyHeal(caster, result.heal, allies, result.allyHealRatio);
  if (result.buff) upsertBuff(caster, result.buff);
  if (result.shield !== undefined && result.shield !== null) applyBarrier(caster, result);
  if (result.targetDebuff && target) applyExhaust(target, result.targetDebuff);
  if (result.removeCrowdControl) applyCleanse(caster);
  if (result.manaRestoreRatio !== undefined && result.manaRestoreRatio !== null) {
    applyClarity(caster, result.manaRestoreRatio, allies, result.allyManaRatio);
  }
}

function applyFlash(entity, pos) {
  entity.x = pos.x;
  entity.z = pos.y;
  entity.targetX = pos.x;
  entity.targetZ = pos.y;
  entity.isMoving = false;
}

function applyHeal(caster, amount, allies, allyRatio = 0.5) {
  caster.hp = Math.min(caster.maxHp, caster.hp + amount);
  const allyHeal = resolveOutgoingHealAmount(Math.round(amount * allyRatio), caster);
  allies.forEach((a) => {
    if (!a || a.isDead || a === caster) return;
    a.hp = Math.min(a.maxHp, a.hp + allyHeal);
  });
}

function applyBarrier(caster, result) {
  caster.shieldHp = result.shield;
  caster.shieldTimer = result.shieldDuration;
}

function applyExhaust(target, debuff) {
  upsertBuff(target, {
    id: debuff.id,
    duration: debuff.duration,
    damageReduction: debuff.damageReduction,
    asReduction: debuff.asReduction
  });
  if (target.statusManager) {
    target.statusManager.addEffect('slow', debuff.duration, debuff.msSlow);
  }
}

function applyCleanse(caster) {
  caster.isStunned = false;
  caster.stunTimer = 0;
  if (caster.statusManager && typeof caster.statusManager.clearCrowdControl === 'function') {
    caster.statusManager.clearCrowdControl();
  } else if (caster.statusManager) {
    caster.statusManager.effects = (caster.statusManager.effects || []).filter(
      (e) => !CC_TYPES.has(e.type)
    );
  }
}

function applyClarity(caster, ratio, allies, allyRatio = ratio) {
  caster.mp = Math.min(caster.maxMp, caster.mp + caster.maxMp * ratio);
  allies.forEach((a) => {
    if (!a || a.isDead || a === caster || !a.maxMp) return;
    a.mp = Math.min(a.maxMp, a.mp + a.maxMp * allyRatio);
  });
}

function upsertBuff(entity, buff) {
  if (!entity.buffs) entity.buffs = [];
  const existing = entity.buffs.find((b) => b.id === buff.id);
  if (existing) {
    Object.assign(existing, buff);
    return;
  }
  entity.buffs.push({ ...buff });
}

function tickShield(entity, dt) {
  if (!(entity.shieldTimer > 0)) return;
  entity.shieldTimer -= dt;
  if (entity.shieldTimer <= 0) {
    entity.shieldTimer = 0;
    entity.shieldHp = 0;
  }
}